import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosGet, axiosPost } from '../api/axios';
import { toast } from 'react-toastify';

export enum VersionedRecordKey {
  CLIENT_ADMIN_TERMS_OF_SERVICE = 'client_admin_terms_of_service',
  CLIENT_ADMIN_PRIVACY_POLICY = 'client_admin_privacy_policy',
  MEMBER_TERMS_OF_SERVICE = 'member_terms_of_service',
  MEMBER_PRIVACY_POLICY = 'member_privacy_policy',
  KINDLY_CHRISTIAN_MEMBER_TERMS_OF_SERVICE = 'kindly_christian_member_terms_of_service',
  KINDLY_CHRISTIAN_MEMBER_PRIVACY_POLICY = 'kindly_christian_member_privacy_policy',
  PEER_TERMS_OF_SERVICE = 'peer_terms_of_service',
  PEER_PRIVACY_POLICY = 'peer_privacy_policy',
  LISTENER_TERMS_OF_SERVICE = 'listener_terms_of_service',
  LISTENER_PRIVACY_POLICY = 'listener_privacy_policy',
  TERMS_OF_SERVICE = 'terms_of_service',
  PEERSONA_ACKNOWLEDGEMENT = 'peersona_acknowledgement',
}

export interface VersionedRecordResponse {
  created_at: string;
  id: number;
  key: string;
  text: string;
  version: number;
}

const getVersionedRecord = async (key: VersionedRecordKey, latest: boolean): Promise<VersionedRecordResponse> => {
  const queryParams: { latest?: boolean } = {};
  if (latest) {
    queryParams.latest = true;
  }
  const response = await axiosGet(`/versioned_records/${key}`, queryParams, 'v3');
  return response.data;
};

export const useVersionedRecord = (key: VersionedRecordKey, latest: boolean = true) => {
  const { data, isLoading, error, refetch, isFetching } = useQuery<VersionedRecordResponse>(
    ['versioned-record', key],
    () => getVersionedRecord(key, latest),
    {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 60 * 24, // 24 hours
      cacheTime: 1000 * 60 * 60 * 24, // 24 hours
    },
  );

  return {
    data,
    isLoading,
    error,
    refetch,
    isFetching,
  };
};

export interface VersionedRecordAcceptanceResponse {
  accepted: boolean;
}

const getVersionedRecordAcceptance = async (key: VersionedRecordKey): Promise<VersionedRecordAcceptanceResponse> => {
  const response = await axiosGet(`/versioned_records/${key}/current`, {}, 'v3');
  return response.data;
};

export const useVersionedRecordAcceptance = (key: VersionedRecordKey) => {
  const { data, isLoading, error, refetch, isFetching } = useQuery<VersionedRecordAcceptanceResponse>(
    ['versioned-record-acceptance', key],
    () => getVersionedRecordAcceptance(key),
    {
      staleTime: 1000 * 60 * 60 * 24, // 24 hours
      cacheTime: 1000 * 60 * 60 * 24, // 24 hours
    },
  );

  return {
    data,
    isLoading,
    error,
    refetch,
    isFetching,
  };
};

export interface CreateVersionedRecordInput {
  text: string;
}

const createVersionedRecordRequest = async (key: VersionedRecordKey, data: CreateVersionedRecordInput) => {
  return await axiosPost(`/versioned_records/${key}`, data, 'v3');
};

export const useCreateVersionedRecord = (key: VersionedRecordKey) => {
  const queryClient = useQueryClient();
  const createVersionedRecord = useMutation(
    (data: CreateVersionedRecordInput) => createVersionedRecordRequest(key, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['versioned-record', key]);
        toast.success('Versioned record created successfully');
      },
    },
  );
  return { createVersionedRecord };
};

const acceptVersionedRecordRequest = async (key: VersionedRecordKey, versionId?: number) => {
  const payload = {
    record_key: key,
    version_id: versionId,
  };

  return await axiosPost(`/versioned_records/accept`, payload, 'v3');
};

export const useAcceptVersionedRecord = () => {
  const queryClient = useQueryClient();
  const acceptVersionedRecord = useMutation(
    ({ versionId, key }: { versionId?: number; key: VersionedRecordKey }) => {
      return acceptVersionedRecordRequest(key, versionId);
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(['versioned-record-acceptance', variables.key]);
      },
    },
  );
  return { acceptVersionedRecord };
};
